// apiClient.js - Cliente base para todas las APIs
import axios from 'axios';
import { config } from '../config/environments';

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: config.API_URL,
  timeout: config.TIMEOUT || 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// ✅ Función helper para limpiar datos de autenticación
const clearAuthData = () => {
  const keysToRemove = [
    'authToken',      // ✅ Cambiado de 'token' a 'authToken'
    'refreshToken',
    'tokenType',
    'userData',       // ✅ Cambiado de 'user' a 'userData'
    'userRole',
    'tokenExpiry'
  ];

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  console.log('🧹 Datos de autenticación limpiados');
};

// Interceptor para agregar token JWT automáticamente
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken'); // ✅ Cambiado de 'token' a 'authToken'
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log de requests en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        data: config.data,
        params: config.params,
        hasToken: !!token
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// ✅ Interceptor mejorado para manejar respuestas y errores con refresh automático
apiClient.interceptors.response.use(
  (response) => {
    // Log de responses en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // ✅ Manejar errores 401 con intento de refresh automático
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          console.log('🔄 Intentando refrescar token automáticamente...');

          // ✅ Hacer petición de refresh sin usar el interceptor
          const refreshResponse = await axios.post(`${config.API_URL}/auth/refresh`, {
            refreshToken: refreshToken
          }, {
            timeout: 10000,
            headers: {
              'Content-Type': 'application/json'
            }
          });

          const refreshData = refreshResponse.data.data || refreshResponse.data;

          // ✅ Actualizar tokens en localStorage
          if (refreshData?.token || refreshData?.accessToken) {
            const newToken = refreshData.token || refreshData.accessToken;
            localStorage.setItem('authToken', newToken);

            // Calcular nueva fecha de expiración
            const expiresIn = refreshData.expiresIn || refreshData.expires_in || 86400;
            const newExpiry = new Date(Date.now() + (expiresIn * 1000)).toISOString();
            localStorage.setItem('tokenExpiry', newExpiry);

            console.log('✅ Token refrescado automáticamente');
          }

          if (refreshData?.refreshToken || refreshData?.refresh_token) {
            const newRefreshToken = refreshData.refreshToken || refreshData.refresh_token;
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // ✅ Reintentar la petición original con el nuevo token
          originalRequest.headers.Authorization = `Bearer ${refreshData.token || refreshData.accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('❌ Error refrescando token automáticamente:', refreshError);

        // ✅ Si falla el refresh, limpiar todo y redirigir
        clearAuthData();

        // Solo redirigir si no estamos ya en login
        if (window.location.pathname !== '/login') {
          console.log('🔐 Redirigiendo al login por token inválido');
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    // ✅ Para otros errores 401 (después de retry fallido) o sin refresh token
    if (error.response?.status === 401) {
      console.warn('🔐 Token expirado o inválido - limpiando sesión');
      clearAuthData();

      // Redirigir al login si no estamos ya ahí
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // ✅ Manejar otros errores de red o servidor
    if (error.response?.status >= 500) {
      console.error('🚨 Error del servidor:', error.response?.status);
    }

    // Log de errores
    console.error('❌ API Error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      url: error.config?.url,
      method: error.config?.method,
      retry: originalRequest._retry
    });

    // ✅ Formatear error mejorado para el frontend
    const formattedError = {
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message || 'Error de conexión',
      errors: error.response?.data?.errors || [],
      data: error.response?.data || null,
      isNetworkError: !error.response,
      isServerError: error.response?.status >= 500,
      isClientError: error.response?.status >= 400 && error.response?.status < 500
    };

    return Promise.reject(formattedError);
  }
);

// ✅ Función helper mejorada para manejar respuestas estándar
export const handleApiResponse = (response) => {
  // Manejar diferentes formatos de respuesta del backend
  if (response.data?.success === false) {
    throw new Error(response.data?.message || 'Error en la operación');
  }

  return {
    success: true,
    data: response.data?.data || response.data,
    message: response.data?.message || 'Operación exitosa',
    status: response.status
  };
};

// ✅ Función helper mejorada para manejar errores
export const handleApiError = (error) => {
  return {
    success: false,
    message: error.message || 'Error desconocido',
    errors: error.errors || [],
    status: error.status || 500,
    isNetworkError: error.isNetworkError || false,
    isServerError: error.isServerError || false,
    isClientError: error.isClientError || false
  };
};

// ✅ Funciones utility adicionales para debugging y monitoreo
export const getAuthStatus = () => {
  const token = localStorage.getItem('authToken');
  const tokenExpiry = localStorage.getItem('tokenExpiry');
  const userData = localStorage.getItem('userData');

  let isExpired = false;
  let timeLeft = 0;

  if (tokenExpiry) {
    const now = Date.now();
    const expiry = new Date(tokenExpiry).getTime();
    isExpired = now >= expiry;
    timeLeft = Math.max(0, expiry - now);
  }

  return {
    hasToken: !!token,
    hasUserData: !!userData,
    isExpired,
    timeLeft,
    timeLeftMinutes: Math.floor(timeLeft / (1000 * 60))
  };
};

// ✅ Función para validar conectividad con la API
export const checkApiHealth = async () => {
  try {
    const response = await axios.get(`${config.API_URL}/health`, { timeout: 5000 });
    return {
      isOnline: true,
      status: response.status,
      message: 'API disponible'
    };
  } catch (error) {
    return {
      isOnline: false,
      status: error.response?.status || 0,
      message: 'API no disponible'
    };
  }
};

export default apiClient;